// Order date rules for the basket and checkout (DEV-51011): which day an order
// is booked for, and which days the engineer may choose instead. Mirrored on
// the backend by orders/fulfillment_dates.py — change the two together.
//
// Dates are UK calendar-day keys ("2026-09-18"), so comparisons are plain
// string comparisons and never depend on the device's time zone.

import {
  addDaysToUKDateKey,
  formatShortMonth,
  formatShortWeekdayDate,
  formatUKDateKey,
} from './datetime'
import type { CartItem, PreferredDateCapability } from '../types'

export type FulfillmentType = 'pickup' | 'delivery'

/** Last choosable day is this many days after the earliest date (decision 5.1). */
export const PREFERRED_DATE_WINDOW_DAYS = 7

/** For lines whose cart response predates the capability fields. */
export const NO_PREFERRED_DATE: PreferredDateCapability = {
  supported: false,
  maxWorkingDaysAfterOrder: null,
}

const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

// Short labels ("Fri 18 Sept") come from the shared en-GB formatter, so the
// checkout row spells a date as the basket header does on the same device.
// Full names never vary, so they are fixed.
const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const
const MONTH_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const

function dateKeyParts(key: string): { year: number; month: number; day: number; weekday: number } {
  const [year, month, day] = key.split('-').map(Number)
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay()
  return { year, month, day, weekday }
}

/** Midday UTC on a UK day key: the same calendar day in UK time, GMT or BST. */
function dateKeyToDate(key: string): Date {
  const { year, month, day } = dateKeyParts(key)
  return new Date(Date.UTC(year, month - 1, day, 12))
}

/** Today as a UK calendar-day key. */
export function todayDateKey(now: Date = new Date()): string {
  return formatUKDateKey(now)
}

/**
 * UK calendar day of a supplier date — plain "2026-09-18" (Wolseley, UKPS,
 * SLA dates) or a datetime such as "2026-09-18T08:00:00Z" (CPS). Null when
 * missing or unreadable. Same rule as orders.services._parse_supplier_date.
 */
export function toDateKey(value: string | null | undefined): string | null {
  if (!value) return null
  if (DATE_KEY_PATTERN.test(value)) return value
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : formatUKDateKey(parsed)
}

/** Monday to Friday, for collection and delivery alike (decision 5.2). */
export function isOpenDay(key: string): boolean {
  const { weekday } = dateKeyParts(key)
  return weekday !== 0 && weekday !== 6
}

export function nextOpenDayOnOrAfter(key: string): string {
  let day = key
  while (!isOpenDay(day)) day = addDaysToUKDateKey(day, 1)
  return day
}

export function nextOpenDayAfter(key: string): string {
  return nextOpenDayOnOrAfter(addDaysToUKDateKey(key, 1))
}

/**
 * The day an order is booked for when nobody changes it (AC1, AC2): the
 * latest line date moved on to the next open day. With no usable date — none
 * given (decision 5.9) or one already in the past — the next open day after
 * today, never today itself.
 */
export function earliestBookableDate(latestItemDate: string | null, today: string): string {
  if (latestItemDate === null || latestItemDate < today) return nextOpenDayAfter(today)
  return nextOpenDayOnOrAfter(latestItemDate)
}

export interface OrderFulfillmentDate {
  /** Booked day when the engineer doesn't choose one; what "Earliest" marks. */
  earliest: string
  /** Latest line date as the supplier gave it — sent as expected_fulfillment_date. */
  latestItemDate: string | null
  /** False when any line lacks a date: the branch confirms the day (5.6, 5.9). */
  isConfirmed: boolean
}

type DatedLine = Pick<CartItem, 'pickupOrderDate' | 'deliveryOrderDate'>

/**
 * Roll the lines that will be placed up to one order date. The single rule the
 * basket header, the checkout date row and the checkout submission all use,
 * replacing the three separate calculations they had before.
 */
export function getOrderFulfillmentDate(
  lines: DatedLine[],
  type: FulfillmentType,
  today: string = todayDateKey(),
): OrderFulfillmentDate {
  const lineDates = lines.map((line) =>
    toDateKey(type === 'pickup' ? line.pickupOrderDate : line.deliveryOrderDate),
  )
  const datedLines = lineDates.filter((date): date is string => date !== null)
  const latestItemDate = datedLines.length > 0
    ? datedLines.reduce((latest, date) => (date > latest ? date : latest))
    : null
  return {
    earliest: earliestBookableDate(latestItemDate, today),
    latestItemDate,
    isConfirmed: lines.length > 0 && datedLines.length === lines.length,
  }
}

export function addWorkingDays(key: string, workingDays: number): string {
  let day = key
  for (let added = 0; added < workingDays; added += 1) day = nextOpenDayAfter(day)
  return day
}

/**
 * Last day that can be chosen (decision 5.1, AC4): 7 days after the earliest,
 * brought in to the supplier's cap in working days after the order date
 * (UKPS: 10) — but never before the earliest itself. Same rule as
 * orders.fulfillment_dates.last_choosable_date.
 */
export function lastChoosableDate(
  earliest: string,
  today: string,
  maxWorkingDaysAfterOrder: number | null,
): string {
  let last = addDaysToUKDateKey(earliest, PREFERRED_DATE_WINDOW_DAYS)
  if (maxWorkingDaysAfterOrder !== null) {
    const cap = addWorkingDays(today, maxWorkingDaysAfterOrder)
    if (cap < last) last = cap
  }
  return last < earliest ? earliest : last
}

export function isChoosableDate(date: string, earliest: string, last: string): boolean {
  return date >= earliest && date <= last && isOpenDay(date)
}

export type CalendarDayStatus = 'available' | 'closed' | 'before-earliest' | 'after-window'

export interface CalendarDay {
  date: string
  status: CalendarDayStatus
  isEarliest: boolean
}

/** Monday-to-Sunday weeks covering the choosable window: two, or one when capped. */
export function calendarWeeks(earliest: string, last: string): CalendarDay[][] {
  const firstMonday = addDaysToUKDateKey(earliest, -((dateKeyParts(earliest).weekday + 6) % 7))
  const weeks: CalendarDay[][] = []
  for (let weekStart = firstMonday; weekStart <= last; weekStart = addDaysToUKDateKey(weekStart, 7)) {
    weeks.push(
      Array.from({ length: 7 }, (_, offset) => {
        const date = addDaysToUKDateKey(weekStart, offset)
        const status: CalendarDayStatus =
          date < earliest ? 'before-earliest'
            : date > last ? 'after-window'
              : isOpenDay(date) ? 'available'
                : 'closed'
        return { date, status, isEarliest: date === earliest }
      }),
    )
  }
  return weeks
}

/** "Fri 18 Sept" */
export function formatShortDate(key: string): string {
  return formatShortWeekdayDate(dateKeyToDate(key))
}

/** "Friday 18 September" */
export function formatLongDate(key: string): string {
  const { month, day, weekday } = dateKeyParts(key)
  return `${WEEKDAY_LONG[weekday]} ${day} ${MONTH_LONG[month - 1]}`
}

/** "Oct" — marks the 1st of a month inside the calendar grid. */
export function formatMonthShort(key: string): string {
  return formatShortMonth(dateKeyToDate(key))
}

/** "September 2026", "September – October 2026", "December 2026 – January 2027" */
export function formatMonthRange(first: string, last: string): string {
  const start = dateKeyParts(first)
  const end = dateKeyParts(last)
  const monthName = (month: number) => MONTH_LONG[month - 1]
  if (start.year !== end.year) {
    return `${monthName(start.month)} ${start.year} – ${monthName(end.month)} ${end.year}`
  }
  if (start.month === end.month) return `${monthName(start.month)} ${start.year}`
  return `${monthName(start.month)} – ${monthName(end.month)} ${end.year}`
}

const DAY_STATUS_REASON: Record<Exclude<CalendarDayStatus, 'available'>, string> = {
  closed: 'closed',
  'before-earliest': 'before the earliest date',
  'after-window': 'too far ahead to book',
}

/** Accessible name for a day button, e.g. "Sunday 27 September, closed" (AC4). */
export function calendarDayLabel(day: CalendarDay, markEarliest: boolean): string {
  const parts = [formatLongDate(day.date)]
  if (markEarliest && day.isEarliest) parts.push('earliest')
  if (day.status !== 'available') parts.push(DAY_STATUS_REASON[day.status])
  return parts.join(', ')
}

export interface CheckoutOrderDates extends OrderFulfillmentDate {
  /** Day shown on the checkout row (null only when there is nothing to show). */
  bookedDate: string | null
  /** Sent as requested_fulfillment_date: null unless the engineer picked another day. */
  requestedDate: string | null
  /** Last day the sheet offers. */
  lastChoosable: string
  /** "Change" is offered: the supplier takes a date and there is another day to pick. */
  canChange: boolean
}

/**
 * Dates for one checkout order card: what the row shows and what the order
 * sends. A chosen day that is no longer choosable (stock refetched and the
 * earliest moved) falls back to the earliest rather than being sent stale.
 */
export function checkoutOrderDates(
  lines: DatedLine[],
  type: FulfillmentType,
  capability: PreferredDateCapability,
  chosenDate: string | undefined,
  today: string = todayDateKey(),
): CheckoutOrderDates {
  const orderDate = getOrderFulfillmentDate(lines, type, today)
  if (!capability.supported) {
    // AC13: shown as the supplier gave it, never moved and never sent as a choice.
    // Its "earliest" is that same unshifted day, so the tag marks it (a CEF
    // Saturday included).
    const earliest = orderDate.latestItemDate ?? orderDate.earliest
    return {
      ...orderDate,
      earliest,
      bookedDate: orderDate.latestItemDate,
      requestedDate: null,
      lastChoosable: earliest,
      canChange: false,
    }
  }
  const lastChoosable = lastChoosableDate(
    orderDate.earliest,
    today,
    capability.maxWorkingDaysAfterOrder,
  )
  const requestedDate =
    chosenDate !== undefined &&
    chosenDate !== orderDate.earliest &&
    isChoosableDate(chosenDate, orderDate.earliest, lastChoosable)
      ? chosenDate
      : null
  return {
    ...orderDate,
    bookedDate: requestedDate ?? orderDate.earliest,
    requestedDate,
    lastChoosable,
    // A cap can leave the earliest as the only open day; a sheet with nothing
    // else to pick is worse than no picker.
    canChange: nextOpenDayAfter(orderDate.earliest) <= lastChoosable,
  }
}

/**
 * Date for a basket group header (AC12): the day checkout will book, or null
 * when no line has a date (the header then shows no date, as today).
 */
export function basketGroupDate(
  lines: DatedLine[],
  type: FulfillmentType,
  supportsPreferredDate: boolean,
  today: string = todayDateKey(),
): string | null {
  const { earliest, latestItemDate } = getOrderFulfillmentDate(lines, type, today)
  if (latestItemDate === null) return null
  return supportsPreferredDate ? earliest : latestItemDate
}

/**
 * Date a basket line shows as its own availability, under the group header
 * (AC12). For suppliers that take a date it follows the order-date rule, so a
 * line never reads a closed day under a header that has moved to Monday;
 * other suppliers' dates are shown as given, like their header.
 */
export function basketLineDate(
  value: string | null | undefined,
  supportsPreferredDate: boolean,
  today: string = todayDateKey(),
): string | null {
  if (!supportsPreferredDate) return value ?? null
  const key = toDateKey(value)
  return key === null ? null : earliestBookableDate(key, today)
}
